import { AxiosError } from 'axios';
import { reduce } from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useQuery } from 'react-query';

import { ordersApi } from 'api';
import {
  OrderCallbackUrlStatus,
  OrderStatus,
  QueryKey,
  StatusCode,
} from 'enums';
import { Order, PaymentFormValues } from 'types';
import { eventUtils } from 'utils';

const COMPLETED_ORDER_STATUSES = [OrderStatus.Cancelled, OrderStatus.Completed];

const CUSTOMER_FIELDS: Array<keyof PaymentFormValues> = [
  'customerCardFirstDigits',
  'customerCardLastDigits',
  'customerName',
  'customerBank',
  'customerPhoneLastDigits',
  'customerUtr',
  'customerIBAN',
  'customerAccountNumber',
];

export type ApiActionOptions = {
  onSuccess: (data: Order) => void;
  onError: (error: AxiosError) => void;
};

type UseOrderDataInput = {
  orderId: string;
  authenticated: boolean;
  setAuthenticationFailed: (val: boolean) => void;
  setPaymentFormValues: Dispatch<SetStateAction<PaymentFormValues>>;
};

export type UseOrderDataResult = {
  order: Order | undefined;
  setOrder: Dispatch<SetStateAction<Order | undefined>>;
  initialOrderFetched: boolean;
  isOrderLoading: boolean;
  refetchOrder: () => void;
  refetchOrderStatus: () => void;
  apiActionOptions: ApiActionOptions;
};

export const useOrderData = ({
  orderId,
  authenticated,
  setAuthenticationFailed,
  setPaymentFormValues,
}: UseOrderDataInput): UseOrderDataResult => {
  const [order, setOrder] = useState<Order>();
  const [initialOrderFetched, setInitialOrderFetched] = useState(false);
  const [timeoutRefetch, setTimeoutRefetch] = useState(false);

  const status = useMemo(() => order?.status as OrderStatus, [order]);

  const isCallbackUrlStatusInProgress = useMemo(
    () =>
      order?.integration?.callbackUrlStatus ===
      OrderCallbackUrlStatus.InProgress,
    [order],
  );

  const shouldRefetch = useMemo(() => {
    if ([OrderStatus.Requisites].includes(status)) {
      return !order?.requisites;
    }
    if (
      [
        OrderStatus.CustomerConfirm,
        OrderStatus.WaitingConfirmation,
        OrderStatus.TraderConfirm,
        OrderStatus.HoldCompleted,
        OrderStatus.Dispute,
      ].includes(status)
    ) {
      return true;
    }
    if (COMPLETED_ORDER_STATUSES.includes(status)) {
      return isCallbackUrlStatusInProgress;
    }
    return false;
  }, [isCallbackUrlStatusInProgress, order?.requisites, status]);

  const apiActionOptions = useMemo<ApiActionOptions>(
    () => ({
      onSuccess: (data: Order) => {
        setOrder(data);
        setPaymentFormValues((prev) =>
          reduce(
            CUSTOMER_FIELDS,
            (result, field) => {
              result[field] = prev[field] || data?.payment?.[field];
              return result;
            },
            { ...prev },
          ),
        );
      },
      onError: (error: AxiosError) => {
        if (error?.response?.status === StatusCode.Unauthorized) {
          setAuthenticationFailed(true);
        } else {
          setInitialOrderFetched(false);
        }
      },
    }),
    [setAuthenticationFailed, setPaymentFormValues],
  );

  const { isLoading: isOrderLoading } = useQuery(
    [QueryKey.Order],
    () => ordersApi.getOne(orderId),
    {
      enabled: authenticated && !initialOrderFetched,
      onSuccess: setOrder,
      onError: (error: AxiosError<{ message: string }>) => {
        setAuthenticationFailed(true);
        eventUtils.trackException({
          message: `Fetch order failed. Status: ${error.response?.status}. ${
            error.response?.data?.message || error.message
          }`,
        });
      },
      onSettled: () => setInitialOrderFetched(true),
    },
  );

  const queryResultOrder = useQuery(
    QueryKey.Order,
    () => ordersApi.getOne(orderId),
    {
      enabled: shouldRefetch,
      refetchInterval: 3000,
      ...apiActionOptions,
    },
  );

  useQuery(QueryKey.Order, () => ordersApi.getOne(orderId), {
    enabled: timeoutRefetch,
    ...apiActionOptions,
  });

  const refetchOrder = useCallback(
    () => queryResultOrder.refetch(),
    [queryResultOrder],
  );
  const refetchOrderStatus = useCallback(() => setTimeoutRefetch(true), []);

  return {
    order,
    setOrder,
    initialOrderFetched,
    isOrderLoading,
    refetchOrder,
    refetchOrderStatus,
    apiActionOptions,
  };
};
